import {type ExtendedItemProps, ItemPicker} from '@github-ui/item-picker-base/ItemPicker'
import type {ItemGroup} from '@github-ui/item-picker-base/shared'
import {useCallback, useMemo, useState} from 'react'
import {graphql, useFragment, useRelayEnvironment} from 'react-relay'

import {LABELS} from '../../../constants/labels'
import {commitClearProjectItemFieldValueMutation} from '../../../mutations/clear-project-item-field-value'
import {commitUpdateProjectItemFieldValueMutation} from '../../../mutations/update-project-item-field-value'
import type {ProjectIterationFieldNode} from '../project-item-section-fields-query-types'
import type {IterationFieldFragment$key} from './__generated__/IterationFieldFragment.graphql'
import {FieldWrapper} from './FieldWrapper'
import {IterationItem} from './IterationItem'
import type {BaseFieldProps} from './Shared'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const IterationFieldFragment = graphql`
  fragment IterationFieldFragment on ProjectV2ItemFieldIterationValue {
    iterationId
    title
    titleHTML
    startDate
    duration
    field {
      ... on ProjectV2IterationField {
        id
        name
        configuration {
          iterations {
            id
            title
            titleHTML
            startDate
            duration
          }
          completedIterations {
            id
            title
            titleHTML
            startDate
            duration
          }
        }
      }
    }
  }
`

type IterationFieldIteration = ProjectIterationFieldNode['configuration']['iterations'][number]

export type IterationFieldConfigFragment = Pick<ProjectIterationFieldNode, 'id' | 'name' | 'configuration'>

const completedGroup: ItemGroup = {groupId: 'completed', header: {title: 'Completed', variant: 'filled'}}
const activeGroup: ItemGroup = {groupId: 'active', header: {title: 'Active', variant: 'filled'}}

type IterationFieldProps = BaseFieldProps<IterationFieldConfigFragment, IterationFieldFragment$key>

export function IterationField({viewerCanUpdate, itemId, projectId, field, value, onIssueUpdate}: IterationFieldProps) {
  const [showInput, setShowInput] = useState(false)
  const environment = useRelayEnvironment()

  const valueData = useFragment(IterationFieldFragment, value)

  const fieldId = field.id
  const fieldName = field.name

  const [filter, setFilter] = useState('')

  const onSelectionChange = useCallback(
    (selection: IterationFieldIteration[]) => {
      if (selection.length === 1) {
        commitUpdateProjectItemFieldValueMutation({
          environment,
          input: {
            fieldId,
            itemId,
            projectId,
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            value: {iterationId: selection[0]!.id},
          },
          onCompleted: onIssueUpdate,
        })
      } else if (selection.length === 0) {
        commitClearProjectItemFieldValueMutation({
          environment,
          input: {
            fieldId,
            itemId,
            projectId,
          },
          onCompleted: onIssueUpdate,
        })
      }
      setShowInput(false)
    },
    [environment, fieldId, itemId, projectId, onIssueUpdate],
  )

  const filterItems = useCallback((v: string) => {
    setFilter(v)
  }, [])

  const items = useMemo(() => {
    const iterations = (field.configuration?.iterations || []).concat(field.configuration?.completedIterations || [])

    if (!filter) return iterations
    return iterations.filter(l => l.title.toLowerCase().indexOf(filter.toLowerCase()) >= 0)
  }, [field.configuration?.completedIterations, field.configuration?.iterations, filter])

  const completedIds = useMemo(() => {
    const iterations = field.configuration?.completedIterations || []
    return new Set(iterations.map(i => i.id))
  }, [field.configuration?.completedIterations])

  const selectedItems = useMemo(() => {
    const activeIterations = field.configuration?.iterations || []
    const completedIterations = field.configuration?.completedIterations || []
    const iterations = activeIterations.concat(completedIterations)

    return iterations.filter(i => i.id === valueData?.iterationId)
  }, [field.configuration?.completedIterations, field.configuration?.iterations, valueData?.iterationId])

  const convertToItemProps = useCallback(
    (option: IterationFieldIteration): ExtendedItemProps<IterationFieldIteration> => {
      return {
        id: option.id,
        children: (
          <IterationItem startDate={option.startDate} durationInDays={option.duration} titleHTML={option.titleHTML} />
        ),
        source: option,
        groupId: completedIds.has(option.id) ? completedGroup.groupId : activeGroup.groupId,
      }
    },
    [completedIds],
  )

  const renderAnchor = useCallback(
    (anchorProps: React.HTMLAttributes<HTMLElement>) => {
      return (
        <FieldWrapper
          showInput={showInput}
          setShowInput={setShowInput}
          anchorProps={anchorProps}
          placeholder={LABELS.emptySections.iterationPlaceholder}
          value={
            valueData ? (
              <IterationItem
                startDate={valueData.startDate}
                durationInDays={valueData.duration}
                titleHTML={valueData.titleHTML}
              />
            ) : null
          }
          name={fieldName}
        />
      )
    },
    [fieldName, showInput, valueData],
  )

  const groups = useMemo(() => {
    return completedIds.size > 0 ? [activeGroup, completedGroup] : [activeGroup]
  }, [completedIds.size])

  if (!viewerCanUpdate)
    return (
      <FieldWrapper
        showInput={showInput}
        setShowInput={setShowInput}
        value={
          valueData ? (
            <IterationItem
              startDate={valueData.startDate}
              durationInDays={valueData.duration}
              titleHTML={valueData.titleHTML}
            />
          ) : null
        }
        placeholder={LABELS.emptySections.noValue(fieldName)}
        canUpdate={false}
        name={fieldName}
      />
    )

  return (
    <ItemPicker
      items={items}
      groups={groups}
      initialSelectedItems={selectedItems}
      filterItems={filterItems}
      getItemKey={i => i.id}
      convertToItemProps={convertToItemProps}
      placeholderText={LABELS.emptySections.iterationFilterPlaceholder}
      selectionVariant="single"
      onSelectionChange={onSelectionChange}
      renderAnchor={renderAnchor}
      height={'large'}
      cssAnchorPositioningSettings={{fallbackStrategy: 'opposite-side'}}
    />
  )
}
